import Avatar from '@material-ui/core/Avatar'
import CardContent from '@material-ui/core/CardContent'
import Divider from '@material-ui/core/Divider'
import Fade from '@material-ui/core/Fade'
import cx from 'clsx'
import cloneDeep from 'lodash/cloneDeep'
import { Fragment, MouseEvent, useEffect, useMemo, useState } from 'react'

import { AnswerAsk } from '@karakuri-web/faq-type'

import { uniqueKey } from '../../utils'
import { Questionnaire } from '../Questionnaire'

import { AnswerActions } from './AnswerActions'
import { AnswerCarousel, getPageIndex } from './AnswerCarousel'
import { AnswerContent } from './AnswerContent'
import { getOperatorEscalationUrlForAnswer } from './getOperatorEscalationUrl'
import {
  AnswerCarouselSelectionState,
  AnswerProps,
  AnswerSelectSelectionState,
  AnswerSelectionPath,
  AnswerUpdateSelectionData,
} from './interface'
import { buildSelectionPath, generateOrderedAnswersExcludeAnotherCards } from './utils'

const dividerStyle = { marginTop: 12, marginBottom: 12 }

function isRootCard(answer: AnswerAsk, cardId: AnswerProps['cardId']): boolean {
  return answer.id === cardId
}

function isCarouselCard(answer: AnswerAsk): boolean {
  return answer.contents.some(c => Boolean(c.carousel))
}

export function Answer({
  contents,
  onClick,
  cardId,
  answers,
  operatorEscalationKeyword,
  karakuriWebRootUrl,
  messages,
  wordsQuestionnaire,
  info,
  session,
  onFeedback,
  skillApi,
}: AnswerProps) {
  const [selectionPath, setSelectionPath] = useState<AnswerSelectionPath>([cardId])
  const [keyAssignmentAnswers, orderedAnswers, defaultSelectSelectionState, actionActionMap] =
    useMemo(() => {
      const actionList: string[][] = []
      const keyAssignmentAnswers: Record<string, AnswerAsk> = {}

      const actionActionMap: Record<
        string /* actionActionId */,
        string /* actionId (＝ answerId) */
      > = {}
      const defaultSelectSelectionState: AnswerSelectSelectionState = {}

      answers.forEach(answer => {
        const actionActionIds = (answer?.actions?.actions || []).map(action => action?.id || '')
        actionList.push(actionActionIds)
        keyAssignmentAnswers[answer.id] = answer

        const isAnswerSelect = actionActionIds.length > 0
        if (isAnswerSelect) {
          // 選択肢ありカードの場合、answer.idとanswer.actions.idは同じ
          defaultSelectSelectionState[answer.id] = {
            currentActionActionId: undefined,
            actionIds: actionActionIds,
          }

          actionActionIds.forEach(actionActionId => {
            actionActionMap[actionActionId] = answer.id
          })
        }
      })
      const orderedAnswers = generateOrderedAnswersExcludeAnotherCards(keyAssignmentAnswers, [
        cardId,
      ])

      return [keyAssignmentAnswers, orderedAnswers, defaultSelectSelectionState, actionActionMap]
    }, [answers, cardId])
  const [carouselSelectionState, setCarouselSelectionState] =
    useState<AnswerCarouselSelectionState>({})
  const [selectSelectionState, setSelectSelectionState] = useState<AnswerSelectSelectionState>({})

  const [operatorEscalationUrl, setOperatorEscalationUrl] = useState('')

  useEffect(() => {
    const url = getOperatorEscalationUrlForAnswer({
      cardId,
      answers: orderedAnswers || [],
      selectionPath,
      karakuriWebRootUrl,
      operatorEscalationKeyword,
      session,
    })
    setOperatorEscalationUrl(url)
  }, [
    cardId,
    orderedAnswers,
    selectionPath,
    karakuriWebRootUrl,
    operatorEscalationKeyword,
    session,
  ])

  useEffect(() => {
    setSelectionPath([cardId])
  }, [cardId])

  const ensureSelectSelectionState = (actionActionId: string): AnswerSelectSelectionState => {
    const cloneSelectSelectionState = cloneDeep(selectSelectionState)
    const actionId = actionActionMap[actionActionId]
    if (!cloneSelectSelectionState[actionId]) {
      cloneSelectSelectionState[actionId] = defaultSelectSelectionState[actionId]
    }
    return cloneSelectSelectionState
  }

  const updateSelectionPath = (data: AnswerUpdateSelectionData) => {
    if (data.carousel) {
      setSelectionPath(
        buildSelectionPath(cardId, keyAssignmentAnswers, data.carousel, selectSelectionState)
      )
    }
    if (data.select) {
      setSelectionPath(
        buildSelectionPath(cardId, keyAssignmentAnswers, carouselSelectionState, data.select)
      )
    }
  }

  const updateSelectActionActionId = (actionActionId: string) => {
    const ensuredSelectSelectionState = ensureSelectSelectionState(actionActionId)
    const actionId = actionActionMap[actionActionId]
    ensuredSelectSelectionState[actionId].currentActionActionId = actionActionId
    setSelectSelectionState(ensuredSelectSelectionState)
    updateSelectionPath({ select: ensuredSelectSelectionState })
  }

  const onActionSelected = (
    e: MouseEvent<unknown>,
    id: string,
    external: boolean,
    uri?: string | null
  ) => {
    e.preventDefault()
    if (external) {
      if (uri) {
        return window.open(uri, '_blank', 'noopener,status=yes,scrollbars=yes,resizable=yes')
      }
      onClick(`/answer/${id}`)
    } else {
      updateSelectActionActionId(id)
    }
  }

  return (
    <div className="faq-answer-content-wrapper">
      <CardContent className={cx('faq-answer-content')}>
        <Avatar
          aria-label="Answer"
          className={cx('faq-answer-a', 'faq-answer-content__a', {
            'faq-answer-a--info': info,
          })}
        >
          {info ? 'i' : 'A'}
        </Avatar>
        {contents && (
          <AnswerContent
            cardId={cardId}
            className="faq-answer-content__answer"
            contents={contents}
            skillApi={skillApi}
          />
        )}
      </CardContent>
      {orderedAnswers && orderedAnswers.length > 0 && (
        <div style={{ display: 'none' }} aria-hidden="true">
          {orderedAnswers
            .filter(a => !isRootCard(a, cardId))
            .map(a => (
              <div key={uniqueKey('seo-answer', cardId, a.id)}>
                <AnswerContent
                  cardId={cardId}
                  className="faq-answer-content__answer"
                  contents={a.contents}
                  skillApi={skillApi}
                />
                {a.actions?.actions?.map((action, index) => {
                  if (!action || action.invalid || !action.external || !action.id) return null
                  return (
                    <a
                      key={uniqueKey('seo-link', cardId, a.id, action.id, index)}
                      href={`/answer/${action.id}`}
                    >
                      {action.label}
                    </a>
                  )
                })}
              </div>
            ))}
        </div>
      )}
      {orderedAnswers && orderedAnswers.length > 0
        ? orderedAnswers.map(a => {
            if (!selectionPath.includes(a.id)) {
              return null
            } else if (isRootCard(a, cardId)) {
              if (isCarouselCard(a)) {
                return (
                  <Fragment key={uniqueKey('fragment-answer', cardId, a.id)}>
                    {a.contents.map(c =>
                      c.carousel ? (
                        <AnswerCarousel
                          key={uniqueKey('answer-carousel', cardId, a.id, c.carousel.id)}
                          index={getPageIndex(c.carousel.id, carouselSelectionState)}
                          baseClassName="faq-answer-content__answer"
                          carousel={c.carousel}
                          skillApi={skillApi}
                          cardId={cardId}
                          onClick={onClick}
                          selectionPath={selectionPath}
                          updateSelectionPath={updateSelectionPath}
                          carouselSelectionState={carouselSelectionState}
                          setCarouselSelectionState={state => setCarouselSelectionState(state)}
                          operatorEscalationKeyword={operatorEscalationKeyword}
                          karakuriWebRootUrl={karakuriWebRootUrl}
                          session={session}
                        />
                      ) : null
                    )}
                  </Fragment>
                )
              } else {
                return (
                  <AnswerActions
                    key={uniqueKey('answer-actions', cardId, a.id)}
                    className="faq-answer-content__button"
                    cardId={cardId}
                    actions={a.actions || null}
                    selectionPath={selectionPath}
                    operatorEscalationKeyword={operatorEscalationKeyword}
                    karakuriWebRootUrl={karakuriWebRootUrl}
                    onActionSelected={onActionSelected}
                    session={session}
                  />
                )
              }
            } else {
              /* isChildCard */
              return (
                <Fade
                  key={uniqueKey('fade-answer', cardId, a.id)}
                  in={selectionPath.includes(a.id)}
                >
                  <>
                    <Divider
                      key={uniqueKey('fade-divider-answer', cardId, a.id)}
                      variant="middle"
                      light
                      style={dividerStyle}
                    />
                    <AnswerContent
                      key={uniqueKey('fade-answer-content', cardId, a.id)}
                      cardId={cardId}
                      className="faq-answer-content__answer"
                      contents={a.contents}
                      skillApi={skillApi}
                    />
                    {isCarouselCard(a) &&
                      a.contents.map(c =>
                        c.carousel ? (
                          <AnswerCarousel
                            key={uniqueKey('answer-carousel', cardId, a.id, c.carousel.id)}
                            index={getPageIndex(c.carousel.id, carouselSelectionState)}
                            baseClassName="faq-answer-content__answer"
                            carousel={c.carousel}
                            skillApi={skillApi}
                            cardId={cardId}
                            onClick={onClick}
                            selectionPath={selectionPath}
                            updateSelectionPath={updateSelectionPath}
                            carouselSelectionState={carouselSelectionState}
                            setCarouselSelectionState={state => setCarouselSelectionState(state)}
                            operatorEscalationKeyword={operatorEscalationKeyword}
                            karakuriWebRootUrl={karakuriWebRootUrl}
                            session={session}
                          />
                        ) : null
                      )}
                    {!isCarouselCard(a) && (
                      <AnswerActions
                        key={uniqueKey('answer-actions', cardId, a.id)}
                        className="faq-answer-content__button"
                        cardId={cardId}
                        actions={a.actions || null}
                        selectionPath={selectionPath}
                        operatorEscalationKeyword={operatorEscalationKeyword}
                        karakuriWebRootUrl={karakuriWebRootUrl}
                        onActionSelected={onActionSelected}
                        session={session}
                      />
                    )}
                  </>
                </Fade>
              )
            }
          })
        : null}
      {!info && (
        <CardContent className="faq-questionnaire-wrapper">
          <Questionnaire
            cardId={cardId}
            onFeedback={onFeedback}
            messages={messages}
            wordsQuestionnaire={wordsQuestionnaire}
            operatorEscalationUrl={operatorEscalationUrl}
            skillApi={skillApi}
          />
        </CardContent>
      )}
    </div>
  )
}
